import { Injectable, inject, signal, computed, OnInit } from '@angular/core';
import { TransactionTypesService } from '../services/transaction-types.service';
import { TransactionType } from '../models/transaction-types';

@Injectable({ providedIn: 'root' })
export class TransactionTypesStore {
  private transactionTypesService = inject(TransactionTypesService);

  // State signals
  transactionTypes = signal<TransactionType[]>([]);
  loading = signal(false);
  lastUpdated = signal<Date | null>(null);

  // Error handling
  error = signal<string | null>(null);

  // Internal map for fast lookups
  private transactionTypesMap = signal<Map<number, TransactionType>>(new Map());

  // Computed values
  hasTransactionTypes = computed(() => this.transactionTypes().length > 0);

  /**
   * Load transaction types from the API
   * @param forceRefresh - Force refresh even if data was recently loaded
   */
  async loadTransactionTypes(forceRefresh = false) {
    // Check if we should skip loading (data exists and not forcing refresh)
    if (!forceRefresh && this.hasTransactionTypes() && this.lastUpdated()) {
      const timeDiff = Date.now() - this.lastUpdated()!.getTime();
      // Skip if loaded within the last 5 minutes (300000 ms)
      if (timeDiff < 300000 * 2) {
        return;
      }
    }

    if (this.loading()) return;

    this.loading.set(true);
    this.error.set(null);

    try {
      const response = await this.transactionTypesService.getTransactionTypes();

      if (response.error) {
        this.error.set(response.error?.message || 'Error loading transaction types');
        return;
      }

      const transactionTypesData = response.data;

      if (transactionTypesData.elements && Array.isArray(transactionTypesData.elements)) {
        this.transactionTypes.set(transactionTypesData.elements);
        this.updateTransactionTypesMap(transactionTypesData.elements);
        this.lastUpdated.set(new Date());
      } else {
        this.error.set('Invalid response format');
      }
    } catch (error) {
      console.error('Error loading transaction types:', error);
      this.error.set('Failed to load transaction types');
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Update the internal map for fast lookups
   */
  private updateTransactionTypesMap(transactionTypes: TransactionType[]) {
    const newMap = new Map<number, TransactionType>();
    transactionTypes.forEach((type) => {
      newMap.set(type.id, type);
    });
    this.transactionTypesMap.set(newMap);
  }

  /**
   * Get a transaction type by ID (optimized with map lookup)
   */
	getTransactionTypeById(id: number): TransactionType | undefined {
    return this.transactionTypesMap().get(id);
  }

  /**
   * Get transaction types by category
   */
  getTransactionTypesByCategory(category: string): TransactionType[] {
    return this.transactionTypes().filter((type) => type.category === category);
  }

  /**
   * Clear all transaction types data
   */
  clearTransactionTypes() {
    this.transactionTypes.set([]);
    this.transactionTypesMap.set(new Map());
    this.error.set(null);
    this.lastUpdated.set(null);
  }

  /**
   * Refresh transaction types data
   */
  async refreshTransactionTypes() {
    return this.loadTransactionTypes(true);
  }
}
